import React, { useState } from 'react';
import { PlusIcon, XIcon, TrashIcon, ClockIcon, FileTextIcon, BellOffIcon, BellRingIcon, UserIcon, CalendarIcon } from 'lucide-react';
import { DocumentNotificationOverride, NotificationEvent } from './NotificationsManagement';
import { StatusBadge } from '../../StatusBadge';
interface DocumentOverridesProps {
  documentOverrides: DocumentNotificationOverride[];
  setDocumentOverrides: React.Dispatch<React.SetStateAction<DocumentNotificationOverride[]>>;
  events: NotificationEvent[];
  locale: string;
}
export function DocumentOverrides({
  documentOverrides,
  setDocumentOverrides,
  events,
  locale
}: DocumentOverridesProps) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingOverride, setEditingOverride] = useState<DocumentNotificationOverride | null>(null);
  const [formData, setFormData] = useState<Partial<DocumentNotificationOverride>>({
    documentId: '',
    documentType: 'invoice',
    documentNumber: '',
    documentName: '',
    overrideType: 'mute',
    affectedEvents: ['all'],
    affectedUsers: ['all'],
    expiresAt: undefined
  });
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  const getDocumentTypeLabel = (type: string) => {
    switch (type) {
      case 'invoice':
        return 'Invoice';
      case 'po':
        return 'Purchase Order';
      case 'requisition':
        return 'Requisition';
      case 'vendor':
        return 'Vendor';
      case 'receipt':
        return 'Receipt';
      default:
        return type;
    }
  };
  const handleAddOverride = () => {
    setEditingOverride(null);
    setFormData({
      documentId: '',
      documentType: 'invoice',
      documentNumber: '',
      documentName: '',
      overrideType: 'mute',
      affectedEvents: ['all'],
      affectedUsers: ['all'],
      expiresAt: undefined
    });
    setIsDrawerOpen(true);
  };
  const handleEditOverride = (override: DocumentNotificationOverride) => {
    setEditingOverride(override);
    setFormData(override);
    setIsDrawerOpen(true);
  };
  const handleDeleteOverride = (id: string) => {
    setDocumentOverrides(documentOverrides.filter(override => override.id !== id));
  };
  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setEditingOverride(null);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  const handleSaveOverride = () => {
    if (!formData.documentNumber || !formData.documentName) {
      // Show validation error
      return;
    }
    const newOverride: DocumentNotificationOverride = {
      id: editingOverride?.id || `override-${Date.now()}`,
      documentId: formData.documentId || `doc-${Date.now()}`,
      documentType: formData.documentType as 'invoice' | 'po' | 'requisition' | 'vendor' | 'receipt',
      documentNumber: formData.documentNumber || '',
      documentName: formData.documentName || '',
      overrideType: formData.overrideType as 'mute' | 'force',
      affectedEvents: formData.affectedEvents || ['all'],
      affectedUsers: formData.affectedUsers || ['all'],
      expiresAt: formData.expiresAt,
      createdBy: editingOverride?.createdBy || 'Current User',
      createdAt: editingOverride?.createdAt || new Date().toISOString()
    };
    if (editingOverride) {
      setDocumentOverrides(documentOverrides.map(override => override.id === editingOverride.id ? newOverride : override));
    } else {
      setDocumentOverrides([...documentOverrides, newOverride]);
    }
    setIsDrawerOpen(false);
    setEditingOverride(null);
  };
  return <div data-id="element-19249">
      <div className="mb-6 flex justify-between items-center" data-id="element-19250">
        <div data-id="element-19251">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-19252">
            Document-Level Notification Overrides
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-19253">
            Override notification settings for specific documents
          </p>
        </div>
        <button type="button" onClick={handleAddOverride} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19254">
          <PlusIcon className="h-4 w-4 mr-2" data-id="element-19255" />
          Add Override
        </button>
      </div>
      <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-19256">
        <ul className="divide-y divide-gray-200" data-id="element-19257">
          {documentOverrides.length === 0 ? <li className="px-6 py-10 text-center" data-id="element-19258">
              <p className="text-sm text-gray-500" data-id="element-19259">
                No document overrides configured yet.
              </p>
              <button type="button" onClick={handleAddOverride} className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19260">
                <PlusIcon className="h-4 w-4 mr-2" data-id="element-19261" />
                Add Your First Override
              </button>
            </li> : documentOverrides.map(override => <li key={override.id} data-id="element-19262">
                <div className="px-4 py-4 sm:px-6" data-id="element-19263">
                  <div className="flex items-center justify-between" data-id="element-19264">
                    <div className="flex items-center" data-id="element-19265">
                      <FileTextIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-19266" />
                      <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-19267">
                        {override.documentNumber}: {override.documentName}
                      </p>
                    </div>
                    <div className="ml-2 flex-shrink-0 flex" data-id="element-19268">
                      <StatusBadge status={override.overrideType === 'mute' ? 'warning' : 'info'} text={override.overrideType === 'mute' ? 'Muted' : 'Forced'} data-id="element-19269" />
                    </div>
                  </div>
                  <div className="mt-2 sm:flex sm:justify-between" data-id="element-19270">
                    <div className="sm:flex" data-id="element-19271">
                      <p className="flex items-center text-sm text-gray-500" data-id="element-19272">
                        {getDocumentTypeLabel(override.documentType)}
                      </p>
                      {override.expiresAt && <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6" data-id="element-19273">
                          <CalendarIcon className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" aria-hidden="true" data-id="element-19274" />
                          Expires: {formatDate(override.expiresAt)}
                        </p>}
                    </div>
                    <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0" data-id="element-19275">
                      <p className="flex items-center" data-id="element-19276">
                        {override.overrideType === 'mute' ? <BellOffIcon className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" data-id="element-19277" /> : <BellRingIcon className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" data-id="element-19278" />}
                        {override.affectedEvents.includes('all') ? 'All Events' : `${override.affectedEvents.length} Event(s)`}
                        {override.affectedUsers.includes('all') ? ' for All Users' : ` for ${override.affectedUsers.length} User(s)`}
                      </p>
                    </div>
                  </div>
                  <div className="mt-2 flex justify-end space-x-2" data-id="element-19279">
                    <button type="button" onClick={() => handleEditOverride(override)} className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19280">
                      Edit
                    </button>
                    <button type="button" onClick={() => handleDeleteOverride(override.id)} className="inline-flex items-center px-2.5 py-1.5 border border-transparent shadow-sm text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500" data-id="element-19281">
                      <TrashIcon className="h-4 w-4 mr-1" data-id="element-19282" />
                      Delete
                    </button>
                  </div>
                </div>
              </li>)}
        </ul>
      </div>
      {/* Override Configuration Drawer */}
      {isDrawerOpen && <div className="fixed inset-0 overflow-hidden z-50" data-id="element-19283">
          <div className="absolute inset-0 overflow-hidden" data-id="element-19284">
            <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseDrawer} data-id="element-19285"></div>
            <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex" data-id="element-19286">
              <div className="relative w-screen max-w-md" data-id="element-19287">
                <div className="h-full flex flex-col bg-white shadow-xl overflow-y-scroll" data-id="element-19288">
                  <div className="px-4 py-6 sm:px-6" data-id="element-19289">
                    <div className="flex items-start justify-between" data-id="element-19290">
                      <h2 className="text-lg font-medium text-gray-900" data-id="element-19291">
                        {editingOverride ? 'Edit Override' : 'Create Document Override'}
                      </h2>
                      <div className="ml-3 h-7 flex items-center" data-id="element-19292">
                        <button type="button" className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500" onClick={handleCloseDrawer} data-id="element-19293">
                          <span className="sr-only" data-id="element-19294">Close panel</span>
                          <XIcon className="h-6 w-6" aria-hidden="true" data-id="element-19295" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 py-6 overflow-y-auto px-4 sm:px-6" data-id="element-19296">
                    <div className="space-y-6" data-id="element-19297">
                      {/* Document Type */}
                      <div data-id="element-19298">
                        <label htmlFor="documentType" className="block text-sm font-medium text-gray-700" data-id="element-19299">
                          Document Type
                        </label>
                        <select id="documentType" name="documentType" value={formData.documentType} onChange={handleChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-19300">
                          <option value="invoice" data-id="element-19301">Invoice</option>
                          <option value="po" data-id="element-19302">Purchase Order</option>
                          <option value="requisition" data-id="element-19303">Requisition</option>
                          <option value="vendor" data-id="element-19304">Vendor</option>
                          <option value="receipt" data-id="element-19305">Receipt</option>
                        </select>
                      </div>
                      {/* Document Number */}
                      <div data-id="element-19306">
                        <label htmlFor="documentNumber" className="block text-sm font-medium text-gray-700" data-id="element-19307">
                          Document Number
                        </label>
                        <input type="text" name="documentNumber" id="documentNumber" value={formData.documentNumber} onChange={handleChange} className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" placeholder="e.g., INV-2023-001" data-id="element-19308" />
                      </div>
                      {/* Document Name */}
                      <div data-id="element-19309">
                        <label htmlFor="documentName" className="block text-sm font-medium text-gray-700" data-id="element-19310">
                          Document Name
                        </label>
                        <input type="text" name="documentName" id="documentName" value={formData.documentName} onChange={handleChange} className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" placeholder="e.g., Office Supplies Invoice" data-id="element-19311" />
                      </div>
                      {/* Override Type */}
                      <div data-id="element-19312">
                        <label className="block text-sm font-medium text-gray-700" data-id="element-19313">
                          Override Type
                        </label>
                        <div className="mt-2 space-y-4" data-id="element-19314">
                          <div className="flex items-center" data-id="element-19315">
                            <input id="override-mute" name="overrideType" type="radio" value="mute" checked={formData.overrideType === 'mute'} onChange={handleChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-19316" />
                            <label htmlFor="override-mute" className="ml-3 block text-sm font-medium text-gray-700" data-id="element-19317">
                              Mute Notifications
                              <span className="block text-sm text-gray-500" data-id="element-19318">
                                Prevent notifications from being sent
                              </span>
                            </label>
                          </div>
                          <div className="flex items-center" data-id="element-19319">
                            <input id="override-force" name="overrideType" type="radio" value="force" checked={formData.overrideType === 'force'} onChange={handleChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-19320" />
                            <label htmlFor="override-force" className="ml-3 block text-sm font-medium text-gray-700" data-id="element-19321">
                              Force Notifications
                              <span className="block text-sm text-gray-500" data-id="element-19322">
                                Always send notifications regardless of user
                                preferences
                              </span>
                            </label>
                          </div>
                        </div>
                      </div>
                      {/* Affected Events */}
                      <div data-id="element-19323">
                        <label className="block text-sm font-medium text-gray-700" data-id="element-19324">
                          Affected Events
                        </label>
                        <div className="mt-2 space-y-2" data-id="element-19325">
                          <div className="flex items-center" data-id="element-19326">
                            <input id="all-events" name="all-events" type="checkbox" checked={formData.affectedEvents?.includes('all')} onChange={e => {
                          if (e.target.checked) {
                            setFormData({
                              ...formData,
                              affectedEvents: ['all']
                            });
                          } else {
                            setFormData({
                              ...formData,
                              affectedEvents: []
                            });
                          }
                        }} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-19327" />
                            <label htmlFor="all-events" className="ml-3 block text-sm font-medium text-gray-700" data-id="element-19328">
                              All Events
                            </label>
                          </div>
                        </div>
                      </div>
                      {/* Affected Users */}
                      <div data-id="element-19329">
                        <label className="block text-sm font-medium text-gray-700" data-id="element-19330">
                          Affected Users
                        </label>
                        <div className="mt-2 space-y-2" data-id="element-19331">
                          <div className="flex items-center" data-id="element-19332">
                            <input id="all-users" name="all-users" type="checkbox" checked={formData.affectedUsers?.includes('all')} onChange={e => {
                          if (e.target.checked) {
                            setFormData({
                              ...formData,
                              affectedUsers: ['all']
                            });
                          } else {
                            setFormData({
                              ...formData,
                              affectedUsers: []
                            });
                          }
                        }} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-19333" />
                            <label htmlFor="all-users" className="ml-3 block text-sm font-medium text-gray-700" data-id="element-19334">
                              All Users
                            </label>
                          </div>
                        </div>
                      </div>
                      {/* Expiration */}
                      <div data-id="element-19335">
                        <label htmlFor="expiresAt" className="block text-sm font-medium text-gray-700" data-id="element-19336">
                          Expiration Date (Optional)
                        </label>
                        <input type="date" name="expiresAt" id="expiresAt" value={formData.expiresAt?.split('T')[0] || ''} onChange={handleChange} className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" data-id="element-19337" />
                        <p className="mt-1 text-sm text-gray-500" data-id="element-19338">
                          Leave empty for no expiration
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex-shrink-0 px-4 py-4 flex justify-end border-t border-gray-200" data-id="element-19339">
                    <button type="button" className="mr-3 bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" onClick={handleCloseDrawer} data-id="element-19340">
                      Cancel
                    </button>
                    <button type="button" className="bg-indigo-600 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" onClick={handleSaveOverride} data-id="element-19341">
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}